import { isOrgAdminRole } from './roleUtils';

/** Collect ROLE_* and permission authorities from the session user. */
export function getUserAuthorities(user) {
  if (!user) return [];
  const roles = Array.isArray(user.roles) ? user.roles : [];
  const permissions = Array.isArray(user.permissions) ? user.permissions : [];
  return [...roles, ...permissions];
}

function normalizeAuthority(value) {
  return String(value || '').replace(/^ROLE_/, '').toUpperCase();
}

/** True if user has a permission whose name starts with RESOURCE_ (or exact match). */
export function hasResourceAccess(user, resource) {
  if (!user || !resource) return false;
  if (user.isSystemAdmin) return true;
  if (user.roles && user.roles.some(isOrgAdminRole)) return true;

  const target = String(resource).toUpperCase();
  const aliases = target === 'PURCHASE' ? ['PURCHASE', 'PROCUREMENT'] : [target];

  return getUserAuthorities(user).some((authority) => {
    const name = String(authority || '');
    // Permission style: PURCHASE_READ
    if (aliases.some((alias) => name === alias || name.startsWith(`${alias}_`))) {
      return true;
    }
    // Role style still present as ROLE_PROC_OPR — handled by module role lists below
    return false;
  });
}

export function hasAnyRole(user, roleNames) {
  if (!user || !Array.isArray(roleNames) || roleNames.length === 0) return false;
  if (user.isSystemAdmin) return true;
  const wanted = new Set(roleNames.map((r) => normalizeAuthority(r)));
  return getUserAuthorities(user).some((authority) => wanted.has(normalizeAuthority(authority)));
}

const OPERATIONAL_ROLES = [
  'PROC_OPR', 'PROC_MGR', 'WBR_OPR', 'WBR_MGR', 'INV_OPR', 'INV_MGR', 'PROD_OPR', 'PROD_MGR',
  'QC_INSPECTOR', 'OQ_MGR', 'SALES_OPR', 'SALES_MGR', 'FIN_OPR', 'FIN_MGR'
];

/** An Account Manager with no other operational role in this org - lands on Account Management, not Dashboard. */
export function isAccountManagerOnly(user) {
  if (!user) return false;
  if (user.isSystemAdmin) return false;
  if (user.roles && user.roles.some(isOrgAdminRole)) return false;
  return hasAnyRole(user, ['ACCOUNT_MGR']) && !hasAnyRole(user, OPERATIONAL_ROLES);
}

/** An affiliate with no other role anywhere in this organization (the usual case: their login belongs to iBigDo's partners organization). */
export function isAffiliateOnly(user) {
  if (!user || !user.isAffiliate) return false;
  if (user.isSystemAdmin) return false;
  if (user.roles && user.roles.some(isOrgAdminRole)) return false;
  return !hasAnyRole(user, OPERATIONAL_ROLES) && !hasAnyRole(user, ['ACCOUNT_MGR']);
}

/**
 * Where a person goes after signing in: Tech Support (not System Admin) to their module, an organization whose plan has ended to Org
 * Admin, an Account Manager with no other role to Account Management, everyone else to the Dashboard.
 */
export function landingPath(user) {
  if (user?.isTechSupport && !user.isSystemAdmin) return '/tech-support';
  if (user?.restricted) return '/org-admin';
  if (isAffiliateOnly(user)) return '/affiliate';
  return isAccountManagerOnly(user) ? '/account-management' : '/dashboard';
}

/**
 * Left-nav module visibility.
 * Matches backend DefaultOrganizationRolePermissions resources / role IDs.
 */
export function canAccessModule(user, moduleKey) {
  if (!user) return false;

  // Platform System Admin module — never grant via Org Admin catch-all below
  if (moduleKey === 'system-admin') {
    return !!user.isSystemAdmin || hasAnyRole(user, ['SYSTEM_ADMIN']);
  }

  // iBigDo staff who answer customers: Tech Support, and System Admin
  if (moduleKey === 'tech-support') {
    return !!user.isSystemAdmin || hasAnyRole(user, ['SYSTEM_ADMIN']) || !!user.isTechSupport;
  }

  // isAccountManager is cross-org (like isSystemAdmin above), unlike `roles` which only reflects
  // whichever org is currently active - someone who's Org Admin of their own org and separately
  // an Account Manager for a different org should still see this nav item. A plain System Admin
  // or Org Admin with no actual assignment anywhere has this as false, so it's never granted by
  // the generic admin catch-alls below. See AuthController.buildAuthResponse.
  if (moduleKey === 'account-management') {
    return !!user.isAccountManager;
  }

  // Cross-org like the above: the Affiliate screen shows a person's referral link, the organizations they referred and what they earned
  if (moduleKey === 'affiliate') {
    return !!user.isAffiliate;
  }

  // A restricted org admin (their organization's subscription plan is missing or expired)
  // may only see the Org Admin screen, regardless of any other role/permission they hold.
  if (user.restricted) {
    return moduleKey === 'org-admin';
  }

  if (user.isSystemAdmin) return true;
  if (user.roles && user.roles.some(isOrgAdminRole)) return true;

  switch (moduleKey) {
    case 'dashboard':
      // An Account Manager with no other operational role skips Dashboard for a customer org
      // they don't operate - Account Management is their whole screen.
      return !isAccountManagerOnly(user) && !isAffiliateOnly(user);
    case 'settings':
      return true;
    case 'store':
      // the Online Store's daily work: the sales roles (the menu also needs the store to be switched on)
      return hasAnyRole(user, ['SALES_OPR', 'SALES_MGR']);
    case 'sales':
      return hasResourceAccess(user, 'SALES')
        || hasResourceAccess(user, 'ORDERS')
        || hasResourceAccess(user, 'CUSTOMERS')
        || hasAnyRole(user, ['SALES_OPR', 'SALES_MGR']);
    case 'purchase':
      return hasResourceAccess(user, 'PURCHASE')
        || hasResourceAccess(user, 'PROCUREMENT')
        || hasResourceAccess(user, 'WEIGHBRIDGE')
        || hasAnyRole(user, ['PROC_OPR', 'PROC_MGR', 'WBR_OPR', 'WBR_MGR', 'FIN_OPR', 'FIN_MGR']);
    case 'inventory':
      // Inventory (stock/GRN/GIN) is INV_* (+ production support roles). Purchase/WBR stay on Purchase.
      return hasResourceAccess(user, 'INVENTORY')
        || hasAnyRole(user, ['INV_OPR', 'INV_MGR', 'PROD_OPR', 'PROD_MGR']);
    case 'quality':
      return hasResourceAccess(user, 'QUALITY')
        || hasAnyRole(user, ['QC_INSPECTOR', 'OQ_MGR']);
    case 'production':
      return hasResourceAccess(user, 'PRODUCTION')
        || hasAnyRole(user, ['PROD_OPR', 'PROD_MGR', 'INV_OPR', 'INV_MGR']);
    case 'accounts':
      // Accounts is limited to Finance roles (Org Admin already allowed above)
      return hasAnyRole(user, ['FIN_OPR', 'FIN_MGR']);
    case 'org-admin':
      return (user.roles && user.roles.some(isOrgAdminRole)) || !!user.isSystemAdmin;
    default:
      return false;
  }
}
